import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import moment from 'moment';
import { MessageService } from 'primeng/api';
import { Subject, Observable } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { HolidayService } from 'src/app/admin/servicies/holiday.service';
import { BaseComponent } from 'src/app/shared/components/base.component';
import { DropdownOption } from 'src/app/shared/models/common/custom-filiters';
import { HolidayResponse } from 'src/app/shared/models/response/appointment/available-slots';
import { Result, ResultOfT } from 'src/app/shared/models/response/result';
import { WebStorageService } from 'src/app/shared/services/web-storage.service';
import { FacilityResponseModel } from '../../../../../shared/models/response/facility/facility-response-model';
import { FacilityService } from '../../../../servicies/facility.service';

@Component({
  selector: 'app-holiday-manage',
  templateUrl: './holiday-manage.component.html',
  styleUrls: ['./holiday-manage.component.sass'],
  standalone: false
})
export class HolidayManageComponent extends BaseComponent implements OnInit, OnDestroy {
  // Public properties (accessible from template)
  public readonly holidayForm: FormGroup<any>;
  public readonly id: number;
  public readonly years: DropdownOption<number>[];
  public facilities: DropdownOption<number>[] = [];
  public minSelectableDate: Date | null = null;
  public maxSelectableDate: Date | null = null;

  // Private properties (internal state)
  private readonly destroy$ = new Subject<void>();
  private readonly loggedInUserId: number;

  // Constants
  private readonly YEAR_RANGE = 2; // How many years ahead to show
  private readonly DATE_FORMAT = 'YYYY-MM-DD';

  constructor(
    private readonly webStorageService: WebStorageService,
    messageService: MessageService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly facilityService: FacilityService,
    private readonly holidayService: HolidayService,
    private readonly formBuilder: FormBuilder
  ) {
    super(messageService);

    const loggedInUser = this.webStorageService.getCurrentUserToken();
    this.loggedInUserId = loggedInUser?.userId ?? 0;
    this.years = this.generateYearOptions();
    this.holidayForm = this.initializeForm();
    this.id = this.getRouteId();
  }

  ngOnInit(): void {
    this.loadFacilities();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Public methods (called from template)
  public onSubmit(): void {
    if (!this.validateForm()) {
      return;
    }

    const holidayModel = this.buildHolidayModel();
    this.saveHoliday(holidayModel);
  }

  public onYearChange(year: number): void {
    this.setDatePickerConstraints(year);
  }

  public onCancel(): void {
    this.navigateToHolidayList();
  }

  // Private methods (internal logic)
  private getRouteId(): number {
    return +this.activatedRoute.snapshot.params['id'] || 0;
  }

  private generateYearOptions(): DropdownOption<number>[] {
    const currentYear = new Date().getFullYear();
    const yearOptions: DropdownOption<number>[] = [];

    for (let i = 0; i < this.YEAR_RANGE; i++) {
      const year = currentYear + i;
      yearOptions.push({
        value: year,
        label: year.toString()
      });
    }

    return yearOptions;
  }

  private initializeForm(): FormGroup {
    return this.formBuilder.group(
      {
        appHolidayName: ['', [Validators.required, Validators.maxLength(255)]],
        appHolidayYear: [null, [Validators.required]],
        appHolidayStartDateTime: [null, [Validators.required]],
        appHolidayEndDateTime: [null, [Validators.required]],
        excludeFacilitiesIds: [[]]
      },
      {
        validators: [this.dateRangeValidator.bind(this)]
      }
    );
  }

  private dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const startDate = control.get('appHolidayStartDateTime')?.value;
    const endDate = control.get('appHolidayEndDateTime')?.value;

    if (startDate && endDate && moment(endDate).isBefore(moment(startDate))) {
      return { dateRange: { message: 'End date must be after start date' } };
    }
    return null;
  }

  private loadFacilities(): void {
    this.facilityService
      .getAllFacilityDropDown()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => this.handleFacilitiesSuccess(result),
        error: (error) => this.handleError(error, 'Failed to load facilities')
      });
  }

  private handleFacilitiesSuccess(result: any): void {
    if (result?.responseModel) {
      this.facilities = this.mapFacilitiesToDropdownOptions(result.responseModel);
      this.loadHolidayForEdit();
    }
  }

  private mapFacilitiesToDropdownOptions(facilities: FacilityResponseModel[]): DropdownOption<number>[] {
    return facilities.map((facility) => ({
      value: facility.crmFacilityId,
      label: facility.crmFacilityName
    }));
  }

  private loadHolidayForEdit(): void {
    if (!this.isEditMode) {
      return;
    }

    this.loading = true;
    this.holidayService
      .GetHolidayById(this.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (result: ResultOfT<HolidayResponse>) => {
          this.processResult<HolidayResponse>(result, () => {
            this.populateFormForEdit(result.responseModel);
          });
        },
        error: (error) => this.handleError(error, 'Failed to load holiday details')
      });
  }

  private populateFormForEdit(holidayDetail: HolidayResponse): void {
    const selectedFacilityIds = this.parseExcludedFacilityIds(holidayDetail.excludeFacilitiesIds);

    this.holidayForm.patchValue({
      appHolidayYear: holidayDetail.appHolidayYear,
      appHolidayName: holidayDetail.appHolidayName,
      appHolidayStartDateTime: new Date(holidayDetail.appHolidayStartDateTime),
      appHolidayEndDateTime: new Date(holidayDetail.appHolidayEndDateTime),
      excludeFacilitiesIds: selectedFacilityIds
    });

    this.setDatePickerConstraints(holidayDetail.appHolidayYear);
  }

  private parseExcludedFacilityIds(facilityIdsString: string | null | undefined): number[] {
    if (!facilityIdsString) {
      return [];
    }

    return facilityIdsString
      .split(',')
      .map(Number)
      .filter((id) => !isNaN(id) && id > 0);
  }

  private validateForm(): boolean {
    this.markAllFieldsAsTouched();
    return this.holidayForm.valid;
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.holidayForm.controls).forEach((key) => {
      this.holidayForm.get(key)?.markAsTouched();
    });
  }

  private saveHoliday(holidayModel: HolidayResponse): void {
    const operation = this.getHolidayOperation(holidayModel);

    this.loading = true;
    operation
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response: Result) => this.handleSaveSuccess(response),
        error: (error) => this.handleError(error, 'Failed to save holiday')
      });
  }

  private getHolidayOperation(holidayModel: HolidayResponse): Observable<Result> {
    return this.isEditMode ? this.holidayService.UpdateHoliday(holidayModel) : this.holidayService.AddHoliday(holidayModel);
  }

  private handleSaveSuccess(response: Result): void {
    if (response?.isSuccess && !response.message) {
      const successMessage = this.isEditMode ? 'Holiday updated successfully' : 'Holiday created successfully';
      this.showSuccessMessage(successMessage);
      this.navigateToHolidayList();
    } else {
      this.showErrorMessage(response.message || 'Operation failed');
    }
  }

  private buildHolidayModel(): HolidayResponse {
    const formData = this.holidayForm.value;

    return {
      appHolidayId: this.id,
      appHolidayName: formData.appHolidayName,
      appHolidayYear: formData.appHolidayYear,
      appHolidayStartDateTime: this.formatDateForApi(formData.appHolidayStartDateTime),
      appHolidayEndDateTime: this.formatDateForApi(formData.appHolidayEndDateTime),
      modifiedByUserInfoId: this.loggedInUserId,
      excludeFacilitiesIds: this.formatFacilityIds(formData.excludeFacilitiesIds)
    } as HolidayResponse;
  }

  private formatDateForApi(date: Date): Date {
    return new Date(moment(date).format(this.DATE_FORMAT));
  }

  private formatFacilityIds(facilityIds: number[]): string {
    return facilityIds?.length > 0 ? facilityIds.join(',') : '';
  }

  private setDatePickerConstraints(year: number): void {
    this.minSelectableDate = moment([year]).startOf('year').toDate();
    this.maxSelectableDate = moment([year]).endOf('year').toDate();
  }

  private navigateToHolidayList(): void {
    this.holidayForm.reset();
    this.holidayService._setTabIndex(4);
    this.router.navigate(['/admin/settings/application'], {
      state: { searchRequestModel: null }
    });
  }

  private handleError(error: any, fallbackMessage: string): void {
    const errorMessage = error?.message || fallbackMessage;
    this.showErrorMessage(errorMessage);
  }

  // Getters
  private get isEditMode(): boolean {
    return this.id > 0;
  }

  public get pageTitle(): string {
    return this.isEditMode ? 'Edit Holiday' : 'Add Holiday';
  }
}
